import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, concatMap, exhaustMap, tap } from 'rxjs/operators';
import { EMPTY, of } from 'rxjs';

import * as SponsorGroupActions from './sponsor-group.actions';
import * as CampaignActions from '../../../campaign/features/campaign.actions';
import { SponsorGroupService } from '../../services/sponsor-group.service';
import { CampaignService } from 'app/campaign/services/campaign.service';
import { SharedService } from 'app/virtual-event/services/shared.service';
import { ToastService } from 'app/toast/services/toast.service';

@Injectable()
export class SponsorGroupEffects {

  getSponsorGroupList$ = createEffect(() => {
    return this.actions$.pipe( 
      ofType(SponsorGroupActions.getSponsorGroupList),
      exhaustMap((action: any) => {
        return this.sponsorGroupService.getList().pipe(
          map(data => SponsorGroupActions.getSponsorGroupListSuccess({ data })),
          catchError(error => of(SponsorGroupActions.getSponsorGroupListFailure({ error }))))
      })
    );
  });

  getSponsorGroup$ = createEffect(() => {
    return this.actions$.pipe( 
      ofType(SponsorGroupActions.getSponsorGroup),
      exhaustMap(({ payload }: any) => {
        return this.sponsorGroupService.get(payload).pipe(
          map(data => SponsorGroupActions.getSponsorGroupSuccess({ data })),
          catchError(error => of(SponsorGroupActions.getSponsorGroupFailure({ error }))))
      })
    );
  });

  createSponsorGroup$ = createEffect(() => {
    return this.actions$.pipe( 
      ofType(SponsorGroupActions.createSponsorGroupList),
      exhaustMap(({ campaign, payload }: any) => {
        return this.sponsorGroupService.create(payload).pipe(
          map(data => SponsorGroupActions.createSponsorGroupListSuccess({ campaign, data })),
          catchError(error => of(SponsorGroupActions.createSponsorGroupListFailure({ error }))))
      })
    );
  });

  createSponsorGroupSuccess$ = createEffect(() => {
    return this.actions$.pipe( 
      ofType(SponsorGroupActions.createSponsorGroupListSuccess),
      concatMap(({ campaign, data: sponsorGroup }: any) => {
        this.sponsorGroupService.sponsorGroupSubject.next(sponsorGroup);
        const getSponsorGroupListAction = SponsorGroupActions.getSponsorGroupList();
        const AddSponsorGroupAction = SponsorGroupActions.AddSponsorGroup({ payload: { campaign, sponsorGroup } });
        return of(getSponsorGroupListAction, AddSponsorGroupAction);
      })
    );
  });

  createSponsorGroupFailure$ = createEffect(() => {
    return this.actions$.pipe( 
      ofType(SponsorGroupActions.createSponsorGroupListFailure),
      map(() => {
        this.sponsorGroupService.sponsorGroupSubject.next(null);
        return null;
      })
    );
  });

  updateSponsorGroup$ = createEffect(() => {
    return this.actions$.pipe( 
      ofType(SponsorGroupActions.updateSponsorGroupList),
      exhaustMap(({ payload }: any) => {
        return this.sponsorGroupService.update(payload).pipe(
          tap(()=> {
            return this.toastService.show('success', 'Sponsor Group has been updated');
          }),
          map(data => SponsorGroupActions.updateSponsorGroupListSuccess({ data })),
          catchError(error => of(SponsorGroupActions.updateSponsorGroupListFailure({ error }))))
      })
    );
  });

  deleteSponsorGroup$ = createEffect(() => {
    return this.actions$.pipe( 
      ofType(SponsorGroupActions.deleteSponsorGroupList),
      exhaustMap(({ campaign, payload }: any) => {
        return this.sponsorGroupService.delete(payload).pipe(
          map(data => SponsorGroupActions.deleteSponsorGroupListSuccess({ campaign, data })),
          catchError(error => of(SponsorGroupActions.deleteSponsorGroupListFailure({ error }))))
      })
    );
  });

  deleteSponsorGroupSuccess$ = createEffect(() => {
    return this.actions$.pipe( 
      ofType(SponsorGroupActions.deleteSponsorGroupListSuccess),
      concatMap(({ campaign }: any) => {
        const action = SponsorGroupActions.AddSponsorGroup({ payload: { campaign, sponsorGroup: { id: null } } });
        return of(action);
      })
    );
  });

  addSponsorGroup$ = createEffect(() => {
    return this.actions$.pipe( 
      ofType(SponsorGroupActions.AddSponsorGroup),
      exhaustMap(({ payload: { campaign, sponsorGroup } }) => {
        return this.campaignService.addSponsorGroup(campaign, sponsorGroup).pipe(
          map(data => SponsorGroupActions.AddSponsorGroupSuccess({ campaign, sponsorGroup, data })),
          catchError(error => of(SponsorGroupActions.AddSponsorGroupFailure({ error }))))
      })
    );
  });

  addSponsorGroupSuccess$ = createEffect(() => {
    return this.actions$.pipe( 
      ofType(SponsorGroupActions.AddSponsorGroupSuccess),
      map(() => {
        return CampaignActions.loadEditCampaignList({
          payload: this.sharedService.token
        });
      })
    );
  });

  constructor(
    private actions$: Actions,
    private sponsorGroupService: SponsorGroupService,
    private campaignService: CampaignService,
    private sharedService: SharedService,
    private toastService: ToastService
  ) {}

}
